import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppThunk } from '../store';
import { firebase, firestore, firestoreLiveQuery, firestoreQuery } from '../firebase';
import { ChatData, MessageData } from '../types';
import { createCrudActions } from '../utils/redux';

let cancelLiveChats: any;
let cancelLiveMessages: any;

interface SupportState {
  chats: ChatData[];
  messages: MessageData[]
  messagesLoading: boolean;
  chatsLoading: boolean;
  loadingMoreChats: boolean;
  loadingMoreMessages: boolean;
  cursorChats: any;
  cursorMessages: any;
}

const initialState: SupportState = {
  chats: [],
  messages: [],
  messagesLoading: false,
  chatsLoading: false,
  loadingMoreChats: false,
  loadingMoreMessages: false,
  cursorChats: null,
  cursorMessages: null,
}


const messageActions = createCrudActions<MessageData, SupportState>({
  idKey: 'id',
  itemsKey: 'messages',
  sort: {
    field: 'createTime',
    order: 'desc',
  }
});

const chatActions = createCrudActions<ChatData, SupportState>({
  idKey: 'id',
  itemsKey: 'chats',
  // sort: {
  //   field: 'createTime',
  //   order: 'desc',
  // }
});


const service = createSlice({
  name: 'support',
  initialState,
  reducers: {
    setChatsLoading(state, action: PayloadAction<boolean>): SupportState {
      return { ...state, chatsLoading: action.payload, }
    },
    setMessagesLoading(state, action: PayloadAction<boolean>): SupportState {
      return { ...state, messagesLoading: action.payload, }
    },
    setLoadingMoreChats(state, action: PayloadAction<boolean>): SupportState {
      return { ...state, loadingMoreChats: action.payload, }
    },
    setLoadingMoreMessages(state, action: PayloadAction<boolean>): SupportState {
      return { ...state, loadingMoreChats: action.payload, }
    },
    setCursorChats(state, action: PayloadAction<any>): SupportState {
      return { ...state, cursorChats: action.payload, }
    },
    setCursorMessages(state, action: PayloadAction<any>): SupportState {
      return { ...state, cursorMessages: action.payload, }
    },
    resetMessages(state): SupportState {
      return { ...state, messages: [], messagesLoading: false, loadingMoreMessages: false, cursorMessages: null, }
    },
    resetChats(state): SupportState {
      return { ...state, chats: [], chatsLoading: false, loadingMoreChats: false, cursorChats: null, }
    },

    onChatAdd: chatActions.onAdd,
    onChatAddMany: chatActions.onAddMany,
    onChatUpdate: chatActions.onUpdate,
    onChatDelete: chatActions.onDelete,

    onMessageAdd: messageActions.onAdd,
    onMessageAddMany: messageActions.onAddMany,
    onMessageUpdate: messageActions.onUpdate,
    onMessageDelete: messageActions.onDelete,
  }
});


const resetMessages = (): AppThunk => async (dispatch, getState) => {
  cancelLiveMessages && cancelLiveMessages();
  dispatch(service.actions.resetMessages());
}

const resetChats = (): AppThunk => async (dispatch, getState) => {
  cancelLiveChats && cancelLiveChats();
  dispatch(service.actions.resetChats());
}

const loadMessages = (chatId: string): AppThunk => async (dispatch, getState) => {
  dispatch(resetMessages());
  dispatch(service.actions.setMessagesLoading(true));

  let query = firestore
    .collection('chats')
    .doc(chatId)
    .collection('chat_messages')
    .orderBy('createTime', 'desc')
    .limit(50);

  cancelLiveMessages = firestoreLiveQuery<MessageData>({
    query,
    onAdd: (item) => {
      dispatch(service.actions.onMessageAdd(item));
    },
    onUpdate: (item) => {
      dispatch(service.actions.onMessageUpdate(item));
    },
    onDelete: (item) => {
      dispatch(service.actions.onMessageDelete(item));
    },
    onInitial: (items, cursor) => {
      dispatch(service.actions.setCursorMessages(cursor));
      dispatch(service.actions.onMessageAddMany(items));
      dispatch(service.actions.setMessagesLoading(false));
    },
    skipInitialOnAdd: true,
  })
}

const loadMoreMessages = (chatId: string): AppThunk => async (dispatch, getState) => {

  const state = getState();

  if (!state.support.cursorMessages || state.support.loadingMoreMessages || state.support.messagesLoading) {
    return;
  }

  dispatch(service.actions.setLoadingMoreMessages(true));

  let query = firestore
    .collection('chats')
    .doc(chatId)
    .collection('chat_messages')
    .orderBy('createTime', 'desc')
    .startAfter(state.support.cursorMessages)
    .limit(50);

  const result = await firestoreQuery<MessageData>(query);

  dispatch(service.actions.onMessageAddMany(result.list));
  dispatch(service.actions.setCursorMessages(result.lastItem));
  dispatch(service.actions.setLoadingMoreMessages(false));
}

const loadChats = (): AppThunk => async (dispatch, getState) => {

  dispatch(service.actions.setChatsLoading(true));

  let query = firestore
    .collection('chats')
    .where('supportChat', '==', true)
    .orderBy('unseenMessagesCount.admin', 'desc')
    .orderBy('updateTime', 'desc')
    .limit(50);

  cancelLiveChats = firestoreLiveQuery<ChatData>({
    query,
    onAdd: (item) => {
      dispatch(service.actions.onChatAdd(item));
    },
    onUpdate: (item) => {
      dispatch(service.actions.onChatUpdate(item));
    },
    onDelete: (item) => {
      dispatch(service.actions.onChatDelete(item));
    },
    onInitial: (items, cursor) => {
      dispatch(service.actions.setCursorChats(cursor));
      dispatch(service.actions.onChatAddMany(items));
      dispatch(service.actions.setChatsLoading(false));
    },
    onError: (e) => {
      console.error(e);
    },
    skipInitialOnAdd: true,
  })
}

const loadMoreChats = (): AppThunk => async (dispatch, getState) => {

  const state = getState();

  if (!state.support.cursorChats || state.support.loadingMoreChats || state.support.chatsLoading) {
    return;
  }

  dispatch(service.actions.setLoadingMoreChats(true));

  let query = firestore
    .collection('chats')
    .where('supportChat', '==', true)
    .orderBy('createTime', 'desc')
    .startAfter(state.support.cursorMessages)
    .limit(50);

  const result = await firestoreQuery<ChatData>(query);

  dispatch(service.actions.onChatAddMany(result.list));
  dispatch(service.actions.setCursorChats(result.lastItem));
  dispatch(service.actions.setLoadingMoreChats(false));
}

interface SendMessageArgs {
  chat: ChatData;
  message: string;
}
const sendMessage = (args: SendMessageArgs): AppThunk => async (dispatch, getState) => {
  const doc = {
    chatId: args.chat.id,
    createTime: firebase.firestore.FieldValue.serverTimestamp(),
    updateTime: firebase.firestore.FieldValue.serverTimestamp(),
    data: args.message,
    fromUserId: 'admin',
    status: 'active',
    supportChat: true,
    type: 'text',
    users: [
      ...args.chat.users,
    ]
  }

  await firestore.collection('chats')
    .doc(args.chat.id)
    .collection('chat_messages')
    .add(doc);
}

const markAllAsRead = (chatId: string): AppThunk => async (dispatch, getState) => {

  await firestore.collection('chats')
    .doc(chatId)
    .update({
      'usersLastSeenMessageTime.admin': firebase.firestore.FieldValue.serverTimestamp(),
      'unseenMessagesCount.admin': 0,
    })
}



export const actions = {

  resetChats,
  resetMessages,
  loadMessages,
  loadMoreMessages,
  loadChats,
  loadMoreChats,
  sendMessage,
  markAllAsRead,
}

export default service.reducer;